import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UserSearch, Building2, ShieldAlert, BellRing, Terminal, CheckCircle2 } from 'lucide-react';

const Offers = () => {
  const navigate = useNavigate();

  const goToRdv = () => {
    navigate('/rdv');
  };

  const mainOffers = [
    {
      title: "Offres Particuliers / Profils exposés",
      desc: "Diagnostic OSINT personnel, analyse des réseaux sociaux et emails, gestion de réputation et identification des risques d'usurpation.",
      icon: UserSearch,
      btnText: "Évaluer votre exposition",
      badge: null
    },
    {
      title: "Offres Entreprises",
      desc: "Audit OSINT complet de l'entreprise, analyse de l'empreinte numérique globale, et évaluation des risques de fraude et phishing.",
      icon: Building2,
      btnText: "Demander un audit",
      badge: "POPULAIRE"
    },
    {
      title: "Offres Dirigeants / RSSI",
      desc: "Audit OSINT exécutif haute confidentialité, conseils stratégiques et restauration de la confidentialité pour profils sensibles.",
      icon: ShieldAlert,
      btnText: "Prendre rendez-vous",
      badge: null
    }
  ];

  return (
    <section className="w-full bg-[#F6FAFD] py-16 px-8 md:py-20">
      <div className="max-w-7xl mx-auto">
        
        {/* Header - 40px */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-[#0A1931] text-[32px] md:text-[40px] font-black font-inter">
            Nos Offres
          </h2>
        </motion.div>

        {/* Top Grid - 3 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {mainOffers.map((offer, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#0A1931] p-8 rounded-[1.5rem] flex flex-col relative overflow-hidden group"
            >
              {offer.badge && (
                <div className="absolute top-6 right-6 bg-[#3b82f6] text-white text-[10px] font-black px-3 py-1 rounded-md">
                  {offer.badge}
                </div>
              )}
              <div className="w-12 h-12 bg-[#1A3D63] rounded-xl flex items-center justify-center text-[#3b82f6] mb-8">
                <offer.icon size={24} />
              </div>
              <h3 className="text-white text-[20px] font-black mb-4 leading-snug">
                {offer.title}
              </h3>
              <p className="text-[#B3CFE5]/70 text-[14px] leading-relaxed mb-10 flex-grow">
                {offer.desc}
              </p>
              <button
                type="button"
                onClick={goToRdv}
                className="w-full py-4 bg-white hover:bg-[#B3CFE5] text-[#0A1931] font-black text-[14px] rounded-xl transition-colors"
              >
                {offer.btnText}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Bottom Grid - Asymmetric 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Veille Offer */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-4 bg-[#0A1931] p-8 rounded-[1.5rem] flex flex-col justify-between"
          >
            <div>
              <div className="w-12 h-12 bg-[#1A3D63] rounded-xl flex items-center justify-center text-[#3b82f6] mb-8">
                <BellRing size={24} />
              </div>
              <h3 className="text-white text-[20px] font-black mb-4">Veille et renseignement</h3>
              <p className="text-[#B3CFE5]/70 text-[14px] leading-relaxed mb-6">
                Surveillance continue des fuites de données, détection anticipée des menaces et alertes en temps réel sur vos actifs numériques.
              </p>
              <div className="mb-8">
                <span className="text-white text-[18px] font-black">Sur abonnement</span>
              </div>
            </div>
            <button
              type="button"
              onClick={goToRdv}
              className="w-full py-4 bg-white hover:bg-[#B3CFE5] text-[#0A1931] font-black text-[14px] rounded-xl transition-colors"
            >
              S'abonner à la veille
            </button>
          </motion.div>

          {/* Audit Technique - Large Card */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-8 bg-[#0A1931] p-8 md:p-12 rounded-[1.5rem] relative overflow-hidden"
          >
            <div className="flex flex-col md:flex-row gap-8 h-full">
              <div className="flex-grow">
                <div className="w-12 h-12 bg-[#1A3D63] rounded-xl flex items-center justify-center text-[#3b82f6] mb-8">
                  <Terminal size={24} />
                </div>
                <h3 className="text-white text-[22px] font-black mb-4">Audit technique / Pré-Pentest</h3>
                <p className="text-[#B3CFE5]/70 text-[14px] leading-relaxed mb-8 max-w-xl">
                  Audit technique non-intrusif pour cartographier votre surface d'attaque externe. Préparation optimale avant un test d'intrusion complet pour maximiser le ROI de vos audits sécurité.
                </p>
                
                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 mb-12">
                  {[
                    "Audit technique non intrusif",
                    "Analyse de la surface d'attaque externe",
                    "Analyse de ports",
                    "Mauvaises configurations visibles"
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle2 size={16} className="text-[#3b82f6]" />
                      <span className="text-[#B3CFE5] text-[13px] font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-center md:justify-start">
                    <button
                      type="button"
                      onClick={goToRdv}
                      className="px-10 py-4 bg-white hover:bg-[#B3CFE5] text-[#0A1931] font-black text-[14px] rounded-xl transition-colors"
                    >
                    Préparer un Pentest
                    </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default Offers;
